import axios from 'axios';
import { getTenantUuid, getUserId, setTenantUuid, setUserId } from '../utils/storage';
import { handleSessionRejection, rejectAxiosSession, sessionResponseError } from './sessionRecovery';

export const PRODUCTION_DOMAINS = ['bizsprite.com', 'aikerr.com', 'web.app', 'firebaseapp.com'];

export const isProductionHostname = () =>
  typeof window !== 'undefined' &&
  PRODUCTION_DOMAINS.some((domain) => window.location.hostname.includes(domain));

// Use HTTPS API endpoint in production, localhost in development
const getAPIBase = () => {
  if (process.env.REACT_APP_API_BASE) {
    return process.env.REACT_APP_API_BASE;
  }
  // In production (Firebase Hosting), use same-origin rewrites
  if (isProductionHostname()) {
    return '/api/enduser';
  }
  // Default for local development
  return 'http://localhost:8001/api/enduser';
};

const API_BASE = getAPIBase();

export const api = axios.create({
  baseURL: API_BASE,
  timeout: 60000,  // 60 seconds for chat API which calls Voice Engine
});

export const getTenantFallbackUUID = () => {
  return process.env.REACT_APP_DEFAULT_TENANT_UUID || null;
};

const resolveTenantUUID = () => {
  return getTenantUuid() || null;
};

const getTenantContextHeaderValue = () => {
  return resolveTenantUUID() || getUserId() || getTenantFallbackUUID();
};

/** Single source for tenant UUID: use from useTenant().getCurrentTenantInfo()?.uuid first, then this. */
export const getTenantUUID = () => {
  return resolveTenantUUID() || getUserId() || getTenantFallbackUUID();
};

let tenantContextSyncPromise = null;
let tenantContextSyncToken = null;

const syncTenantContextFromSession = async () => {
  if (resolveTenantUUID()) return resolveTenantUUID();
  const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
  if (!token) return null;

  if (!tenantContextSyncPromise || tenantContextSyncToken !== token) {
    tenantContextSyncToken = token;
    const pending = fetch('/api/users/me', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (response) => {
        if (!response.ok) {
          handleSessionRejection(response.status, token);
          return null;
        }
        const userInfo = await response.json();
        if (localStorage.getItem('authToken') !== token) return null;
        if (userInfo?.user_id || userInfo?.id) {
          setUserId(userInfo.user_id || userInfo.id);
        }
        if (userInfo?.tenant_uuid || userInfo?.tenant_id) {
          setTenantUuid(userInfo.tenant_uuid || userInfo.tenant_id);
          return userInfo.tenant_uuid || userInfo.tenant_id;
        }
        return null;
      })
      .catch(() => null)
      .finally(() => {
        if (tenantContextSyncPromise === pending) {
          tenantContextSyncPromise = null;
          tenantContextSyncToken = null;
        }
      });
    tenantContextSyncPromise = pending;
  }
  return tenantContextSyncPromise;
};

// The backend authenticates every tenant API call; X-Aiker-User alone is not
// enough. Every client that sends the tenant header also sends the session.
export const attachSessionToken = (config) => {
  const authToken = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
  if (authToken && !config.headers['Authorization']) {
    config.headers['Authorization'] = `Bearer ${authToken}`;
  }
  return config;
};

// 🔒 Tenant Context Interceptor
// Automatically inject X-Aiker-User header for multi-tenant isolation
api.interceptors.request.use(
  async (config) => {
    const requestToken = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
    if (!resolveTenantUUID() && typeof window !== 'undefined' && localStorage.getItem('authToken')) {
      await syncTenantContextFromSession();
      if (localStorage.getItem('authToken') !== requestToken) {
        throw new axios.CanceledError('Session changed before request');
      }
    }
    const contextHeaderValue = getTenantContextHeaderValue();
    if (contextHeaderValue) {
      config.headers['X-Aiker-User'] = contextHeaderValue;
    } else {
      console.warn('[API] Missing tenant context; set user id, tenant_uuid, or REACT_APP_DEFAULT_TENANT_UUID');
    }
    return attachSessionToken(config);
  },
  (error) => Promise.reject(error)
);

/** Extract human-readable message from API error (axios or similar). Use for toasts and user-facing messages. */
export const extractApiError = (err) => {
  const detail = err?.response?.data?.detail;
  if (detail != null) return typeof detail === 'string' ? detail : JSON.stringify(detail);
  if (err?.response?.data?.message) return err.response.data.message;
  if (err?.message) return err.message;
  return 'An unexpected error occurred';
};

// Only a rejection of the current request's bearer invalidates its session.
api.interceptors.response.use(
  (res) => res,
  rejectAxiosSession
);

export const InfoAPI = {
  get: async () => (await api.get('/info')).data,
  put: async (payload) => (await api.put('/info', payload)).data,
};

export const PartnerSSOAPI = {
  exchange: async (token) => {
    const response = await fetch('/api/enduser/partner-sso/exchange', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ token }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new Error(typeof data.detail === 'string' ? data.detail : data.detail?.message || 'Automatic login failed');
      err.response = { status: response.status, data };
      throw err;
    }
    return data;
  },
};

export const ClientsAPI = {
  list: async () => (await api.get('/clients')).data,
  create: async (payload) => (await api.post('/clients', payload)).data,
  update: async (id, payload) => (await api.patch(`/clients/${id}`, payload)).data,
  remove: async (id) => (await api.delete(`/clients/${id}`)).data,
};

export const RoutesAPI = {
  list: async () => (await api.get('/routes')).data,
  create: async (payload) => (await api.post('/routes', payload)).data,
  update: async (id, payload) => (await api.patch(`/routes/${id}`, payload)).data,
  remove: async (id) => (await api.delete(`/routes/${id}`)).data,
  uploadCSV: async (file) => {
    const form = new FormData();
    form.append('file', file);
    return (await api.post('/routes/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
  },
};

// Contacts (persons) directory uses /contacts endpoints
export const ContactsAPI = {
  list: async () => (await api.get('/contacts')).data,
  create: async (payload) => (await api.post('/contacts', payload)).data,
  update: async (id, payload) => (await api.put(`/contacts/${id}`, payload)).data,
  remove: async (id) => (await api.delete(`/contacts/${id}`)).data,
  bulkImport: async (payload) => (await api.post('/contacts/bulk-import', payload)).data,
};

// Phase 3: Departments (read-only list for transfer targets)
export const DepartmentsAPI = {
  list: async (companyId = null) => (
    await api.get('/departments', { params: companyId ? { company_id: companyId } : {} })
  ).data,
};

// Phase 3: Queues (call queues, blind transfer only)
export const QueuesAPI = {
  list: async () => (await api.get('/queues')).data,
  create: async (payload) => (await api.post('/queues', payload)).data,
  update: async (id, payload) => (await api.put(`/queues/${id}`, payload)).data,
  remove: async (id) => (await api.delete(`/queues/${id}`)).data,
};

export const KBAPI = {
  list: async (params = {}) => (await api.get('/kb', { params })).data,
  testSearch: async (payload) => {
    const token = localStorage.getItem('authToken');
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    return (await api.post('/kb/test-search', payload, { headers })).data;
  },
  create: async (payload) => (await api.post('/kb', payload)).data,
  update: async (id, payload) => (await api.patch(`/kb/${id}`, payload)).data,
  remove: async (id) => (await api.delete(`/kb/${id}`)).data,
  getTemplateMetadata: async () => (await api.get('/kb/template-metadata')).data,
  downloadTemplate: async () => (await api.get('/kb/template.csv', { responseType: 'blob' })).data,
  downloadStructuredTemplate: async () => (await api.get('/kb/template.xlsx', { responseType: 'blob' })).data,
  upload: async (file) => {
    const form = new FormData();
    form.append('file', file);
    return (await api.post('/kb/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
  },
};

export const QATrainingAPI = {
  getCoverage: async () => (await api.get('/qa-training/coverage')).data,
  listRuns: async (params = {}) => (await api.get('/qa-training/runs', { params })).data,
  getRun: async (id) => (await api.get(`/qa-training/runs/${id}`)).data,
  createRun: async (payload, idempotencyKey = null) => {
    const requestKey = idempotencyKey
      || (typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `qa-training-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    return (await api.post('/qa-training/runs', payload, {
      headers: { 'Idempotency-Key': requestKey },
    })).data;
  },
  cancelRun: async (id) => (await api.post(`/qa-training/runs/${id}/cancel`)).data,
  listCandidates: async (params = {}) => (await api.get('/qa-training/candidates', { params })).data,
  updateCandidateLocale: async (id, locale, payload) => (
    await api.patch(`/qa-training/candidates/${id}/locales/${locale}`, payload)
  ).data,
  approveCandidate: async (id, payload) => (
    await api.post(`/qa-training/candidates/${id}/approve`, payload)
  ).data,
  rejectCandidate: async (id, payload) => (
    await api.post(`/qa-training/candidates/${id}/reject`, payload)
  ).data,
  publishCandidates: async (candidateIds) => (
    await api.post('/qa-training/publications', { candidate_ids: candidateIds })
  ).data,
};

export const ProductsAPI = {
  summary: async () => (await api.get('/products/summary')).data,
  qaAgentSummary: async () => (await api.get('/products/qa-agent/summary')).data,
  runQaAgent: async (payload) => (await api.post('/products/qa-agent/run', payload)).data,
  collectQaEvidence: async (payload = {}) => (await api.post('/products/qa-agent/evidence/collect', payload)).data,
  listQaEvidence: async (params = {}) => (await api.get('/products/qa-agent/evidence', { params })).data,
  refreshProductFamilies: async () => (await api.post('/products/qa-agent/families/refresh')).data,
  listProductFamilies: async (params = {}) => (await api.get('/products/qa-agent/families', { params })).data,
  listQaJobs: async () => (await api.get('/products/qa-agent/jobs')).data,
  getQaJob: async (id) => (await api.get(`/products/qa-agent/jobs/${id}`)).data,
  listQaSuggestions: async (params = {}) => (await api.get('/products/qa-agent/suggestions', { params })).data,
  approveQaSuggestion: async (id) => (await api.post(`/products/qa-agent/suggestions/${id}/approve`)).data,
  rejectQaSuggestion: async (id) => (await api.post(`/products/qa-agent/suggestions/${id}/reject`)).data,
  bulkApproveQaSuggestions: async (payload) => (await api.post('/products/qa-agent/suggestions/bulk-approve', payload)).data,
  bulkRejectQaSuggestions: async (payload) => (await api.post('/products/qa-agent/suggestions/bulk-reject', payload)).data,
  applyApprovedQaJob: async (id) => (await api.post(`/products/qa-agent/jobs/${id}/apply-approved`)).data,
  downloadTemplate: async () => (await api.get('/products/import/template.xlsx', { responseType: 'blob' })).data,
  downloadExport: async () => (await api.get('/products/export.xlsx', { responseType: 'blob' })).data,
  previewImport: async (file) => {
    const form = new FormData();
    form.append('file', file);
    return (await api.post('/products/import/preview', form, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
  },
  listImportJobs: async () => (await api.get('/products/import/jobs')).data,
  getImportJob: async (jobId) => (await api.get(`/products/import/jobs/${jobId}`)).data,
  applyImportJob: async (jobId) => (await api.post(`/products/import/jobs/${jobId}/apply`)).data,
  list: async (params = {}) => (await api.get('/products', { params })).data,
  listTypes: async () => (await api.get('/products/types')).data,
  detail: async (sku) => (await api.get(`/products/${encodeURIComponent(sku)}`)).data,
  update: async (sku, payload) => (await api.patch(`/products/${encodeURIComponent(sku)}`, payload)).data,
  listFaqs: async (sku) => (await api.get(`/products/${encodeURIComponent(sku)}/faqs`)).data,
  createFaq: async (sku, payload) => (await api.post(`/products/${encodeURIComponent(sku)}/faqs`, payload)).data,
  updateFaq: async (sku, faqId, payload) => (await api.patch(`/products/${encodeURIComponent(sku)}/faqs/${faqId}`, payload)).data,
  removeFaq: async (sku, faqId) => (await api.delete(`/products/${encodeURIComponent(sku)}/faqs/${faqId}`)).data,
  listAfterSalesRules: async () => (await api.get('/products/after-sales-rules')).data,
  createAfterSalesRule: async (payload) => (await api.post('/products/after-sales-rules', payload)).data,
  updateAfterSalesRule: async (ruleId, payload) => (await api.patch(`/products/after-sales-rules/${ruleId}`, payload)).data,
  removeAfterSalesRule: async (ruleId) => (await api.delete(`/products/after-sales-rules/${ruleId}`)).data,
};

const getExtensionApiBase = () => {
  // Extension endpoints are exposed under /api (not under /api/enduser).
  if (process.env.REACT_APP_API_BASE) {
    let base = process.env.REACT_APP_API_BASE.replace('/enduser', '');
    // Always use HTTPS in production (when page is HTTPS)
    if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
      base = base.replace(/^http:/, 'https:');
    }
    return base;
  }
  // In production (Firebase Hosting), use same-origin rewrites
  if (isProductionHostname()) {
    return '/api';
  }
  // Default for local development
  return 'http://localhost:8001/api';
};

const extensionApi = axios.create({
  baseURL: getExtensionApiBase(),
  timeout: 15000,
});

// Reuse tenant header via request interceptor - use the same logic as api
extensionApi.interceptors.request.use((config) => {
  return syncTenantContextFromSession().then(() => {
  const contextHeaderValue = getTenantContextHeaderValue();
  if (contextHeaderValue) {
    config.headers['X-Aiker-User'] = contextHeaderValue;
  } else {
    console.warn('[API] Missing tenant context for extension API');
  }
  return attachSessionToken(config);
  });
}, (error) => Promise.reject(error));

export const ExtensionsAPI = {
  list: async () => (await extensionApi.get('/agent-extensions/')).data,
  create: async (payload) => (await extensionApi.post('/agent-extensions/', payload)).data,
  update: async (extension, payload) => (await extensionApi.put(`/agent-extensions/${extension}`, payload)).data,
  remove: async (extension) => (await extensionApi.delete(`/agent-extensions/${extension}`)).data,
  listStatuses: async () => (await extensionApi.get('/extensions/')).data,  // Add trailing slash to avoid 307 redirect
  getStatus: async (extension) => (await extensionApi.get(`/extensions/${extension}/status`)).data,
  start: async (extension) => (await extensionApi.post(`/extensions/${extension}/start`)).data,
  stop: async (extension) => (await extensionApi.post(`/extensions/${extension}/stop`)).data,
  restart: async (extension) => (await extensionApi.post(`/extensions/${extension}/restart`)).data,
};

export const TransferLogsAPI = {
  list: async (status) => (await api.get('/transfer-logs', { params: { status } })).data,
  create: async (payload) => (await api.post('/transfer-logs', payload)).data,
};

export const CalendarAPI = {
  listEvents: async (params) => (await api.get('/calendar/events', { params })).data,
  createEvent: async (payload) => (await api.post('/calendar/events', payload)).data,
  updateEvent: async (id, payload) => (await api.patch(`/calendar/events/${id}`, payload)).data,
  deleteEvent: async (id) => (await api.delete(`/calendar/events/${id}`)).data,
  syncGoogle: async (timeMin = null, timeMax = null, integrationId = null) => {
    const payload = { time_min: timeMin, time_max: timeMax, integration_id: integrationId };
    return (await api.post('/calendar/google/sync', payload)).data;
  },
  syncTeams: async (timeMin = null, timeMax = null, integrationId = null) => {
    const payload = { time_min: timeMin, time_max: timeMax, integration_id: integrationId };
    return (await api.post('/calendar/teams/sync', payload)).data;
  },
  syncApple: async (timeMin = null, timeMax = null, integrationId = null) => {
    const payload = { time_min: timeMin, time_max: timeMax, integration_id: integrationId };
    return (await api.post('/calendar/apple/sync', payload)).data;
  },
  syncCalendly: async (timeMin = null, timeMax = null, integrationId = null) => {
    const payload = { time_min: timeMin, time_max: timeMax, integration_id: integrationId };
    return (await api.post('/calendar/calendly/sync', payload)).data;
  },
  listAccounts: async () => (await api.get('/calendar/accounts')).data,
  connectApple: async (payload) => (await api.post('/calendar/accounts/apple', payload)).data,
  disconnectAccount: async (integrationId) => (await api.delete(`/calendar/accounts/${integrationId}`)).data,
  googleStart: async (accountType = null) => {
    const frontendUrl =
      typeof window !== 'undefined'
        ? `${window.location.origin}/app`
        : undefined;
    const params = {};
    if (frontendUrl) params.frontend_url = frontendUrl;
    if (accountType) params.account_type = accountType;
    return (
      await api.get('/calendar/google/start', {
        params: Object.keys(params).length ? params : undefined,
      })
    ).data;
  },

  // Calendly OAuth
  calendlyStart: async () => {
    const frontendUrl =
      typeof window !== 'undefined'
        ? `${window.location.origin}/app`
        : undefined;
    return (
      await api.get('/calendar/calendly/start', {
        params: frontendUrl ? { frontend_url: frontendUrl } : undefined,
      })
    ).data;
  },

  // Calendly integration
  getCalendlyEventTypes: async (integrationId = null) => (await api.get('/calendar/calendly/event-types', { params: integrationId ? { integration_id: integrationId } : undefined })).data,
  createCalendlyLink: async (eventTypeUri, title, startTime, endTime, description = null, integrationId = null) => {
    const payload = {
      event_type_uri: eventTypeUri,
      title,
      start_time: startTime,
      end_time: endTime,
      description,
      integration_id: integrationId,
    };
    return (await api.post('/calendar/calendly/create-link', payload)).data;
  },
  googleCapabilityCheck: async (integrationId, declaredType = null) => {
    const payload = { integration_id: integrationId, declared_type: declaredType };
    return (await api.post('/calendar/google/capability-check', payload)).data;
  },
  teamsCapabilityCheck: async (integrationId, declaredType = null) => {
    const payload = { integration_id: integrationId, declared_type: declaredType };
    return (await api.post('/calendar/teams/capability-check', payload)).data;
  },
  // Booking (self-managed + official hybrid)
  listBookingResources: async () => (await api.get('/bookings/resources')).data,
  listBookingServiceTypes: async () => (await api.get('/bookings/service-types')).data,
  createBookingResource: async (payload) => (await api.post('/bookings/resources', payload)).data,
  createBookingServiceType: async (payload) => (await api.post('/bookings/service-types', payload)).data,
  updateBookingResource: async (id, payload) => (await api.patch(`/bookings/resources/${id}`, payload)).data,
  deleteBookingResource: async (id) => (await api.delete(`/bookings/resources/${id}`)).data,
  updateBookingServiceType: async (id, payload) => (await api.patch(`/bookings/service-types/${id}`, payload)).data,
  deleteBookingServiceType: async (id) => (await api.delete(`/bookings/service-types/${id}`)).data,
  previewBookingSlots: async (resourceId, date, params = {}) =>
    (await api.get(`/bookings/resources/${resourceId}/slots`, { params: { date, ...params } })).data,
  checkBookingAvailability: async (payload) => (await api.post('/bookings/availability', payload)).data,
  holdBooking: async (payload) => (await api.post('/bookings/hold', payload)).data,
  confirmBooking: async (bookingId, params = {}) => (await api.post(`/bookings/${bookingId}/confirm`, null, { params })).data,
  cancelBooking: async (bookingId) => (await api.post(`/bookings/${bookingId}/cancel`)).data,
  retryBookingSync: async (bookingId) => (await api.post(`/bookings/${bookingId}/retry-sync`)).data,
  updateAccount: async (integrationId, payload) =>
    (await api.patch(`/calendar/accounts/${integrationId}`, payload)).data,
  updateBookingPage: async (integrationId, bookingPayload = null) => {
    const toSingleEntry = (raw) => {
      if (!raw) return null;
      if (typeof raw === 'string') return { url: raw };
      if (typeof raw !== 'object') return null;
      const url = raw.url || raw.booking_page_url || raw.href || null;
      if (!url) return null;
      return {
        url,
        label: raw.label || raw.name || raw.title || undefined,
        is_default: raw.is_default,
      };
    };

    let body = {};
    if (Array.isArray(bookingPayload)) {
      body = { booking_pages: bookingPayload };
    } else if (typeof bookingPayload === 'string') {
      body = { booking_page_url: bookingPayload };
    } else {
      const single = toSingleEntry(bookingPayload);
      // Non-array payload may come from UI as { url, label, is_default }.
      // Always normalize to booking_pages so backend does not receive object in booking_page_url.
      body = single ? { booking_pages: [single] } : { booking_page_url: null };
    }

    return (await api.patch(`/calendar/accounts/${integrationId}/booking-page`, body)).data;
  },
  teamsStart: async () => (await api.get('/calendar/teams/start')).data,
  outlookStart: async () => (await api.get('/calendar/outlook/start')).data,
  // Conflicts & metrics (backend endpoints exist)
  listConflicts: async () => (await api.get('/bookings/conflicts')).data,
  getBookingMetrics: async () => (await api.get('/bookings/metrics')).data,
  // Bookings list (endpoint may not exist yet; caller must handle 404)
  listBookings: async (params) => (await api.get('/bookings', { params })).data,
};

export const CallbackRequestsAPI = {
  list: async (params) => (await api.get('/callback-requests', { params })).data,
  count: async (status = 'pending') => (await api.get('/callback-requests/count', { params: { status } })).data,
  create: async (payload) => (await api.post('/callback-requests', payload)).data,
  updateStatus: async (id, newStatus) => (await api.patch(`/callback-requests/${id}`, { new_status: newStatus })).data,
  updateFollowup: async (id, payload) => (await api.patch(`/callback-requests/${id}`, payload)).data,
  delete: async (id) => (await api.delete(`/callback-requests/${id}`)).data,
};

export const VoicemailAPI = {
  list: async (params) => (await api.get('/voicemail-messages', { params })).data,
  get: async (id) => (await api.get(`/voicemail-messages/${id}`)).data,
  updateStatus: async (id, newStatus) => (await api.patch(`/voicemail-messages/${id}`, { new_status: newStatus })).data,
  delete: async (id) => (await api.delete(`/voicemail-messages/${id}`)).data,
};

export const SMSLogsAPI = {
  list: async (params) => (await api.get('/sms-logs', { params })).data,
  get: async (id) => (await api.get(`/sms-logs/${id}`)).data,
};

// Customer texts to the shared SMS number that match no ticket or callback (owned by this tenant).
export const SMSInboxAPI = {
  list: async (params) => (await api.get('/sms-inbox', { params })).data,
  setHandled: async (id, handled) => (await api.post(`/sms-inbox/${id}/handled`, null, { params: { handled } })).data,
};

// Merchant workspace notification center and assistant (docs/current/WORKSPACE_ASSISTANT_PLAN_2026-10-28.md).
export const WorkspaceAPI = {
  config: async () => (await api.get('/workspace/config')).data,
  notifications: async () => (await api.get('/workspace/notifications')).data,
  markRead: async (id) => (await api.post(`/workspace/notifications/${id}/read`)).data,
  markAllRead: async () => (await api.post('/workspace/notifications/read-all')).data,
  status: async () => (await api.get('/workspace/status')).data,
  ask: async (payload) => (await api.post('/workspace/assistant', payload)).data,
};

export const ScriptTemplatesAPI = {
  list: async (params) => (await api.get('/scripts', { params })).data,
  update: async (id, payload) => (await api.put(`/scripts/${id}`, payload)).data,
};

export const PublicAPI = {
  sendDocuments: async (payload) => (await api.post('/public/send-documents', payload)).data,
  /** Public lead capture (no login). POST body matches backend PublicRegisterIn. */
  registerLead: async (payload) => (await api.post('/public/register', payload)).data,
};

// Flow / monitoring logs (lives under /api/flow-management)
const getFlowApiBase = () => {
  if (process.env.REACT_APP_API_BASE) {
    return process.env.REACT_APP_API_BASE.replace('/enduser', '');
  }
  if (isProductionHostname()) {
    return '/api';
  }
  return 'http://localhost:8001/api';
};

const flowApi = axios.create({
  baseURL: getFlowApiBase(),
  timeout: 20000,
});
flowApi.interceptors.request.use(attachSessionToken, (error) => Promise.reject(error));

// Service-record corrections (9/28 checklist B2). record_ref = call_sessions.id.
const refsParams = (key, refs) => {
  const params = new URLSearchParams();
  (refs || []).filter(Boolean).slice(0, 200).forEach((ref) => params.append(key, ref));
  return params;
};

export const CorrectionsAPI = {
  issueTypes: async () => (await api.get('/service-records/issue-types')).data,
  list: async (params = {}) => (await api.get('/service-records/corrections', { params })).data,
  corrected: async (recordRefs) => (await api.get('/service-records/corrected', { params: refsParams('record_refs', recordRefs) })).data,
  create: async (payload) => (await api.post('/service-records/corrections', payload)).data,
  update: async (id, issues) => (await api.patch(`/service-records/corrections/${encodeURIComponent(id)}`, { issues })).data,
  remove: async (id) => (await api.delete(`/service-records/corrections/${encodeURIComponent(id)}`)).data,
  events: async (id) => (await api.get(`/service-records/corrections/${encodeURIComponent(id)}/events`)).data,
  // E4 review: per-status totals, owner-only approve/pause/resume, and the rules the AI receives.
  counts: async () => (await api.get('/service-records/corrections/counts')).data,
  review: async (id, action) => (await api.post(`/service-records/corrections/${encodeURIComponent(id)}/review`, { action })).data,
  preview: async (id) => (await api.get(`/service-records/corrections/${encodeURIComponent(id)}/preview`)).data,
};

// Customers and their history across calls, chats, callbacks and bookings (plan 2026-10-11 B2).
export const CustomersAPI = {
  list: async (params = {}) => (await api.get('/customers', { params })).data,
  get: async (id) => (await api.get(`/customers/${encodeURIComponent(id)}`)).data,
};

// Website Chatbox (B4): owner settings and Chatbox service records.
export const ChatWidgetAPI = {
  get: async () => (await api.get('/chat-widget')).data,
  save: async (payload) => (await api.put('/chat-widget', payload)).data,
  chats: async (params = {}) => (await api.get('/service-records/chats', { params })).data,
  chat: async (id) => (await api.get(`/service-records/chats/${encodeURIComponent(id)}`)).data,
  // A visitor's voice message or image (chat_media), as a Blob for an object URL.
  media: async (id, messageId) => (await api.get(
    `/service-records/chats/${encodeURIComponent(id)}/media/${encodeURIComponent(messageId)}`, { responseType: 'blob' })).data,
  // Human handoff (W-H phase 1): staff replies, take/release, nav badge, owner settings.
  reply: async (id, payload) => (await api.post(`/service-records/chats/${encodeURIComponent(id)}/reply`, payload)).data,
  handoff: async (id, action) => (await api.post(`/service-records/chats/${encodeURIComponent(id)}/handoff`, { action })).data,
  awaitingCount: async () => (await api.get('/service-records/chat-awaiting-count')).data,
  getHandoff: async () => (await api.get('/chat-widget/handoff')).data,
  saveHandoff: async (payload) => (await api.put('/chat-widget/handoff', payload)).data,
};

// Team sub-accounts (B3).
export const TeamAPI = {
  me: async () => (await api.get('/team/me')).data,
  members: async () => (await api.get('/team/members')).data,
  createMember: async (payload) => (await api.post('/team/members', payload)).data,
  updateMember: async (id, payload) => (await api.patch(`/team/members/${encodeURIComponent(id)}`, payload)).data,
};

export const LogsAPI = {
  /** includeMessages=false skips per-call transcripts (fast list). Detail page uses fetchCallLogById. */
  fetchCallLogs: async (extension, limit = 50, contextUUID = null, includeMessages = true, filters = {}) => {
    let headerValue = contextUUID;
    if (!headerValue) {
      headerValue = getTenantContextHeaderValue();
    }
    const headers = headerValue ? { 'X-Aiker-User': headerValue } : undefined;
    return (
      await flowApi.get('/flow-management/call-logs', {
        params: {
          extension, limit, include_messages: includeMessages,
          ...Object.fromEntries(Object.entries(filters || {}).filter(([, value]) => value !== undefined && value !== null && value !== '')),
        },
        headers,
      })
    ).data;
  },
  fetchCallLogById: async (callSessionId, contextUUID = null) => {
    let headerValue = contextUUID;
    if (!headerValue) {
      headerValue = getTenantContextHeaderValue();
    }
    const headers = headerValue ? { 'X-Aiker-User': headerValue } : undefined;
    return (
      await flowApi.get(`/flow-management/call-logs/by-id/${encodeURIComponent(callSessionId)}`, { headers })
    ).data;
  },
  fetchCallRawLog: async (callSessionId, lines = 400, contextUUID = null) => {
    let headerValue = contextUUID;
    if (!headerValue) {
      headerValue = getTenantContextHeaderValue();
    }
    const headers = headerValue ? { 'X-Aiker-User': headerValue } : undefined;
    return (await flowApi.get(`/flow-management/call-logs/${callSessionId}/raw-log`, { params: { lines }, headers })).data;
  },
  fetchRecordingUrl: async (callSessionId, contextUUID = null) => {
    let headerValue = contextUUID;
    if (!headerValue) {
      headerValue = getTenantContextHeaderValue();
    }
    const headers = headerValue ? { 'X-Aiker-User': headerValue } : undefined;
    return (await flowApi.get(`/flow-management/call-logs/${encodeURIComponent(callSessionId)}/recording`, { headers })).data;
  },
  transcribeRecording: async (callSessionId, contextUUID = null, payload = {}) => {
    let headerValue = contextUUID;
    if (!headerValue) {
      headerValue = getTenantContextHeaderValue();
    }
    const headers = headerValue ? { 'X-Aiker-User': headerValue } : undefined;
    return (
      await flowApi.post(
        `/flow-management/call-logs/${encodeURIComponent(callSessionId)}/transcribe-recording`,
        payload,
        { headers, timeout: 120000 }
      )
    ).data;
  },
  fetchStatusLog: async (extension, lines = 400) => {
    return (await flowApi.get(`/flow-management/status-logs/${extension}`, { params: { lines } })).data;
  },
};

export const UserAPI = {
  getCurrentUser: async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('No auth token found');
    }
    const response = await fetch('/api/users/me', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      throw sessionResponseError(response, token, `Failed to fetch current user (${response.status})`);
    }
    return response.json();
  },
};

export const ImpersonationAPI = {
  validate: async (token) => (
    await api.get('/impersonation/validate', {
      headers: { Authorization: `Bearer ${token}` },
    })
  ).data,
};

// Tenant Settings API (lives under /api/tenant-settings)
const getTenantSettingsApiBase = () => {
  if (process.env.REACT_APP_API_BASE) {
    return process.env.REACT_APP_API_BASE.replace('/enduser', '');
  }
  if (isProductionHostname()) {
    return '/api';
  }
  return 'http://localhost:8001/api';
};

const tenantSettingsApi = axios.create({
  baseURL: getTenantSettingsApiBase(),
  timeout: 45000, // 45s for tenant settings (voice test needs ~30s)
});

// Reuse tenant header via request interceptor
tenantSettingsApi.interceptors.request.use((config) => {
  const authToken = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
  if (authToken && !config.headers['Authorization']) {
    config.headers['Authorization'] = `Bearer ${authToken}`;
  }
  const tenantUUID = resolveTenantUUID();
  const fallbackUUID = getTenantFallbackUUID();
  if (tenantUUID || fallbackUUID) {
    config.headers['X-Aiker-User'] = tenantUUID || fallbackUUID;
  } else {
    console.warn('[API] Missing tenant context for tenant-settings API');
  }
  return config;
}, (error) => Promise.reject(error));

tenantSettingsApi.interceptors.response.use((response) => response, rejectAxiosSession);

export const TenantSettingsAPI = {
  // Use trailing slash to avoid Starlette's redirect-to-slash behavior (can produce http:// redirects behind proxies).
  get: async (tenantId) => (await tenantSettingsApi.get('/tenant-settings/', { params: { tenant_id: tenantId } })).data,
  update: async (tenantId, payload) => (await tenantSettingsApi.put('/tenant-settings/', payload, { params: { tenant_id: tenantId } })).data,
  listVoices: async () => (await tenantSettingsApi.get('/tenant-settings/voices')).data,
  testGeminiLiveVoice: async (tenantId, payload) => {
    return await tenantSettingsApi.post('/tenant-settings/gemini-live-test', payload, {
      params: { tenant_id: tenantId },
      responseType: 'blob',
      timeout: 60000, // 60s for voice synthesis
    });
  },
};

export const ChatAPI = {
  sendMessage: async (content, conversationId = null) => {
    const response = await api.post('/chat', { 
      content,
      conversation_id: conversationId 
    });
    return response.data;
  },
  
  getConversations: async () => {
    const response = await api.get('/chat/conversations');
    return response.data;
  },
  
  listConversations: async () => {
    const response = await api.get('/chat/conversations');
    return response.data;
  },
  
  getConversationHistory: async (conversationId) => {
    const response = await api.get(`/chat/conversations/${conversationId}`);
    return response.data;
  },
  
  deleteConversation: async (conversationId) => {
    const response = await api.delete(`/chat/conversations/${conversationId}`);
    return response.data;
  },
};

export default api;
